import React, { forwardRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, CheckCircle2, PlayCircle, FilePlus } from 'lucide-react';
import { getTestStatusInfo, formatDateTime } from '../utils/helpers';

const TestHistoryItem = forwardRef(({
  test,
  isSelected,
  onClick,
  index,
  compact = false
}, ref) => {
  const navigate = useNavigate();
  const { studentId } = useParams(); // URL에서 studentId 가져오기
  const statusInfo = getTestStatusInfo(test);

  // compact: 학습(/students-hub) 세로형 패널에서 카드 세로 높이를 줄인다 (학습분석은 기본값 유지)
  const c = {
    pad: compact ? 'p-2.5' : 'p-[14.4px]',
    round: compact ? 'rounded-2xl' : 'rounded-3xl',
    shadow: compact ? 'shadow-sm' : 'shadow-lg',
    headMb: compact ? 'mb-1' : 'mb-2',
    iconBtn: compact ? 'w-6 h-6 min-w-[24px] min-h-[24px]' : 'w-[32px] h-[32px] min-w-[32px] min-h-[32px]',
    iconSize: compact ? 20 : 32,
    title: compact ? 'text-[12px]' : 'text-[14px]',
    sub: compact ? 'text-[10px]' : 'text-xs',
    date: compact ? 'text-[8px]' : 'text-[9px]',
    cell: compact ? 'p-1' : 'p-2',
    val: compact ? 'text-xs' : 'text-sm',
  };
  
  const handleIconClick = (e) => {
    e.stopPropagation(); // 카드 클릭 이벤트 전파 방지
    
    // 필요한 정보가 있는지 확인
    const student_id = studentId || test.student_id;
    
    if (student_id && test.stamp_id && test.exam_id) {
      navigate(`/student/${student_id}/stamp/${test.stamp_id}/result/${test.exam_id}`);
    } else {
      console.error('필요한 정보가 부족합니다:', { 
        student_id, 
        stamp_id: test.stamp_id, 
        exam_id: test.exam_id 
      });
    }
  };
  
  return (
    <div
      ref={ref}
      className={`cursor-pointer border bg-white ${c.round} ${c.pad} ${c.shadow} transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20
        ${isSelected ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'}`}
      onClick={onClick}
    >
      <div>
        <div className={`${c.headMb} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            {/* 상태 아이콘 */}
            <button
              onClick={handleIconClick}
              className={`${c.iconBtn} flex items-center justify-center rounded-full transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20`}
              title="결과 상세보기"
            >
              {test.exam_status === 999 ? (
                <CheckCircle2 className="text-green-600" size={c.iconSize} />
              ) : test.exam_status === 400 ? (
                <PlayCircle className="text-orange-500" size={c.iconSize} />
              ) : (
                <FilePlus className="text-blue-500" size={c.iconSize} />
              )}
            </button>
            <div>
              <h3 className={`${c.title} font-semibold`}>{test.tree_name}</h3>
              <p className={`${c.sub} text-gray-500`}>{test.edu_ver}</p>
            </div>
          </div>
          <div className={`rounded-3xl ${c.date} font-medium text-gray-500`}>
            <div>생성: {formatDateTime(test.created_at || test.exam_created_at)}</div>
            <div>수정: {formatDateTime(test.updated_at || test.exam_updated_at)}</div>
          </div>
        </div>
      </div>
      <div>
        {/* 테스트 정보 (모바일용 4열) */}
        <div className="grid grid-cols-4 gap-1 text-xs">
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">TID</div>
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <div className={`${c.val} font-semibold text-gray-800`}>{test.exam_id}</div>
                  <span className={`${c.val} font-medium text-green-600`}>
                    {test.exam_type === 'alpha' ? 'α' :
                     test.exam_type === 'beta' ? 'β' :
                     test.exam_type === 'gamma' ? 'γ' :
                     test.exam_type === 'delta' ? 'δ' :
                     test.exam_type === 'custom' ? '𝐂' :
                     test.exam_type === 'epsilon' ? 'ε' :
                     test.exam_type || '-'}
                  </span>
                </div>
                {(test.exam_round !== undefined && test.exam_round !== null) && (
                  <div className="text-xs text-gray-500">({test.exam_round}회)</div>
                )}
              </div>
            </div>
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">난이도</div>
              <div className="flex flex-col items-center justify-center">
                <div className={`${c.val} font-semibold text-gray-800`}>
                  {test.difficulty || '-'}
                </div>
              </div>
            </div>
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">문제수</div>
              <div className="flex flex-col items-center justify-center">
                <div className={`${c.val} font-semibold text-gray-800`}>
                  {test.length || ((test.correct_count || 0) + (test.wrong_count || 0))}
                </div>
              </div>
            </div>
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">정답률</div>
              <div className="flex flex-col items-center">
                <div className={`${c.val} font-semibold text-green-600`}>
                  {(test.length > 0 || ((test.correct_count || 0) + (test.wrong_count || 0)) > 0) ? 
                    `${Math.round(((test.correct_count || 0) / (test.length || ((test.correct_count || 0) + (test.wrong_count || 0)))) * 100)}%` : 
                    '-'
                  }
                </div>
                <div className="text-[8px] font-normal">
                  <span className="text-gray-400">(</span>
                  <span className="text-primary font-bold">{test.correct_count || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-danger font-bold">{test.wrong_count || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-black font-bold">{test.length || ((test.correct_count || 0) + (test.wrong_count || 0))}</span>
                  <span className="text-gray-400">)</span>
                </div>
              </div>
            </div>
                  </div>
      </div>
    </div>
  );
});

TestHistoryItem.displayName = 'TestHistoryItem';

export default TestHistoryItem; 